import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users, Brain, FileText, TrendingUp, UserCheck, Target, RefreshCw, UserPlus,
} from '../components/icons';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { FreeProfileCoachDashboard } from './coach/FreeProfileCoachDashboard';
import {
  fetchCoachId,
  fetchCoachStats,
  fetchCoachAssessmentsOverTime,
  fetchCoachCharacteristicAverages,
  fetchCoachHarmonicAverages,
  fetchCoachRecentCompletions,
  fetchCoachRespondentBreakdown,
  fetchCoachClientRecentActivity,
  type CoachStats,
  type CoachMonthlyAssessment,
  type CharacteristicAverage,
  type HarmonicAverage,
  type CoachRecentCompletion,
  type RespondentBreakdown,
  type ClientProgressActivity,
} from '../lib/coach-dashboard';
import { CoachAssessmentsChart } from '../components/coach/CoachAssessmentsChart';
import { CoachCharacteristicsOverview } from '../components/coach/CoachCharacteristicsOverview';
import { CoachHarmonicOverview } from '../components/coach/CoachHarmonicOverview';
import { CoachRecentCompletions } from '../components/coach/CoachRecentCompletions';
import { CoachRespondentBreakdown } from '../components/coach/CoachRespondentBreakdown';
import { CoachClientProgress } from '../components/coach/CoachClientProgress';
import { InviteRespondentModal, ShareLinkCard } from '../components/coach/InviteRespondentModal';

export function CoachDashboard() {
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [coachTier, setCoachTier] = useState('basic');
  const [isFreeProfile, setIsFreeProfile] = useState(false);
  const [assessmentCode, setAssessmentCode] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [respondentLimit, setRespondentLimit] = useState(50);
  const [stats, setStats] = useState<CoachStats>({
    totalRespondents: 0,
    completedAssessments: 0,
    pendingAssessments: 0,
    completionRate: 0,
    aiAnalysesGenerated: 0,
    retakesCompleted: 0,
  });
  const [assessmentsOverTime, setAssessmentsOverTime] = useState<CoachMonthlyAssessment[]>([]);
  const [characteristicAverages, setCharacteristicAverages] = useState<CharacteristicAverage[]>([]);
  const [harmonicAverages, setHarmonicAverages] = useState<HarmonicAverage[]>([]);
  const [recentCompletions, setRecentCompletions] = useState<CoachRecentCompletion[]>([]);
  const [respondentBreakdown, setRespondentBreakdown] = useState<RespondentBreakdown>({
    completed: 0, pending: 0, withAiAnalysis: 0, withRetakes: 0,
  });
  const [clientActivity, setClientActivity] = useState<ClientProgressActivity[]>([]);

  useEffect(() => {
    if (!user?.id) return;
    loadDashboard();
  }, [user?.id]);

  const loadDashboard = async () => {
    if (!user?.id) return;

    try {
      const { data: coachRecord } = await supabase
        .from('coaches')
        .select('tier, max_respondents, account_plan, assessment_code')
        .eq('user_id', user.id)
        .maybeSingle();

      if (coachRecord?.account_plan === 'FREE_PROFILE_COACH') {
        setIsFreeProfile(true);
        setIsLoading(false);
        return;
      }

      setAssessmentCode(coachRecord?.assessment_code || null);

      setCoachTier(coachRecord?.tier || 'basic');
      setRespondentLimit(coachRecord?.max_respondents || 50);

      const id = await fetchCoachId(user.id);

      if (id) {
        const [
          statsData,
          assessmentsData,
          characteristicsData,
          harmonicsData,
          completionsData,
          breakdownData,
          activityData,
        ] = await Promise.all([
          fetchCoachStats(id),
          fetchCoachAssessmentsOverTime(id),
          fetchCoachCharacteristicAverages(id),
          fetchCoachHarmonicAverages(id),
          fetchCoachRecentCompletions(id),
          fetchCoachRespondentBreakdown(id),
          fetchCoachClientRecentActivity(id),
        ]);

        setStats(statsData);
        setAssessmentsOverTime(assessmentsData);
        setCharacteristicAverages(characteristicsData);
        setHarmonicAverages(harmonicsData);
        setRecentCompletions(completionsData);
        setRespondentBreakdown(breakdownData);
        setClientActivity(activityData);
      }
    } catch (err) {
      console.error('Error loading coach data:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <LoadingSpinner message="Loading coach dashboard..." />;
  }

  const statCards = [
    { label: 'Total Respondents', value: stats.totalRespondents, icon: Users, color: 'bg-success-40' },
    { label: 'Completed', value: stats.completedAssessments, icon: FileText, color: 'bg-teal-50' },
    { label: 'Pending', value: stats.pendingAssessments, icon: UserCheck, color: 'bg-orange-40' },
    { label: 'Completion Rate', value: `${stats.completionRate}%`, icon: Target, color: 'bg-brand-40' },
    { label: 'AI Analyses', value: stats.aiAnalysesGenerated, icon: Brain, color: 'bg-teal-50' },
    { label: 'Retakes', value: stats.retakesCompleted, icon: RefreshCw, color: 'bg-success-50' },
  ];

  if (isFreeProfile) {
    return <FreeProfileCoachDashboard />;
  }

  return (
    <div className="min-h-screen freud-bg py-8 px-6">
      <AnimatePresence>
        {showInviteModal && assessmentCode && (
          <InviteRespondentModal
            assessmentCode={assessmentCode}
            coachName={user?.user_metadata?.full_name || user?.email || ''}
            onClose={() => setShowInviteModal(false)}
          />
        )}
      </AnimatePresence>
      <div className="max-w-7xl mx-auto space-y-8">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="freud-heading-2xl text-chocolate-900">Dashboard</h1>
            <p className="freud-text-base text-chocolate-500">Your coaching overview</p>
          </div>
          <button
            onClick={() => setShowInviteModal(true)}
            className="freud-btn-primary flex items-center gap-2 flex-shrink-0"
          >
            <UserPlus className="w-4 h-4" />
            Invite Someone
          </button>
        </div>

        {assessmentCode && (
          <ShareLinkCard assessmentCode={assessmentCode} />
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {statCards.map((stat, index) => (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.06 }}
              className="freud-card-elevated p-5"
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`p-2.5 ${stat.color} rounded-xl shadow-soft`}>
                  <stat.icon className="w-5 h-5 text-white" />
                </div>
                <TrendingUp className="w-4 h-4 text-gray-30" />
              </div>
              <div className="text-text-2xl text-gray-80 mb-0.5">{stat.value}</div>
              <div className="text-text-xs text-gray-40">{stat.label}</div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
          >
            <CoachAssessmentsChart data={assessmentsOverTime} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45 }}
          >
            <CoachRecentCompletions completions={recentCompletions} />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.48 }}
        >
          <CoachClientProgress activities={clientActivity} />
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
          >
            <CoachCharacteristicsOverview data={characteristicAverages} />
          </motion.div>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
          >
            <CoachRespondentBreakdown data={respondentBreakdown} />
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6 }}
        >
          <CoachHarmonicOverview data={harmonicAverages} />
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.65 }}
          className="bg-gradient-to-r from-sage-600 to-sage-700 rounded-2xl p-6 text-white shadow-soft-lg"
        >
          <div className="flex items-center justify-between">
            <div>
              <h3 className="freud-heading-base text-white mb-1">Respondent Capacity</h3>
              <p className="freud-text-sm text-sage-100">
                {stats.totalRespondents} / {respondentLimit} respondents used
              </p>
            </div>
            <div className="text-right">
              <div className="freud-heading-2xl text-white">
                {Math.round((stats.totalRespondents / (respondentLimit || 1)) * 100)}%
              </div>
              <p className="freud-text-sm text-sage-100">capacity used</p>
            </div>
          </div>
          <div className="mt-4 h-2 bg-sage-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-white rounded-full transition-all"
              style={{ width: `${(stats.totalRespondents / (respondentLimit || 1)) * 100}%` }}
            />
          </div>
        </motion.div>
      </div>
    </div>
  );
}
